import { IPublicClientApplication } from "@azure/msal-browser";
import { getNativeAuthClient } from "../auth/msalInstance";
import { beginSignOut, clearLocalSession, getAuthMethod } from "../auth/session";

const LOGGED_OUT_PATH = "/logged-out";

/**
 * Signs the user out of whichever sign-in path they used and ends on /logged-out.
 * Native sign-in creates no Entra browser session, so it signs out locally. The hosted page does,
 * so that path goes through Entra's logout endpoint.
 */
export const logOut = async (instance: IPublicClientApplication): Promise<void> => {
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;
  const nativeClient = getNativeAuthClient();

  // Before anything is cleared: the app must not render its signed-out routes while this runs,
  // or the sign-in gateway starts a fresh redirect and Entra signs the user back in.
  beginSignOut();

  if (getAuthMethod() === "native" && nativeClient) {
    try {
      const current = nativeClient.getCurrentAccount();
      if (current.isCompleted()) {
        await current.data?.signOut();
      }
    } catch (error) {
      console.error("Native sign-out failed; clearing the local session", error);
    }

    await clearLocalSession();
    window.location.assign(LOGGED_OUT_PATH);
    return;
  }

  // The token cache stays until logoutRedirect has built its request, so Entra gets an id token
  // hint and signs this account out without prompting. /logged-out clears the rest.
  await clearLocalSession({ keepTokenCache: true });

  try {
    await instance.logoutRedirect({
      account,
      postLogoutRedirectUri: `${window.location.origin}${LOGGED_OUT_PATH}`,
    });
  } catch (error) {
    console.error("Hosted sign-out failed", error);
    window.location.assign(LOGGED_OUT_PATH);
  }
};
